import { PickupCorporateScheduleConfigComponents } from "@/app/infrastructures/dependencies/modules/PickupCorporateScheduleConfigComponents";
import store from "@/store";
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from "vuex-module-decorators";
import { PickupCorporateConfigPresenter } from "@/app/ui/presenters/PickupCorporateConfigPresenter";
import { PickupCorporateScheduleConfigPresenter } from "@/app/ui/presenters/PickupCorporateScheduleConfigPresenter";
import { PickupCorporateConfig } from "@/domain/entities/PickupCorporateConfig";
import { PickupCorporateScheduleConfig } from "@/domain/entities/PickupCorporateScheduleConfig";
import { Snackbar } from "@/domain/entities/MainApp";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { container } from "tsyringe";

function getBackendMessage(error: unknown): string {
  const response = error as {
    response?: { data?: { message?: unknown } };
  };
  const message = response.response?.data?.message;
  return typeof message === "string" ? message : JSON.stringify(message || "");
}

PickupCorporateScheduleConfigComponents.init();

@Module({
  namespaced: true,
  dynamic: true,
  store,
  name: "pickup-corporate-schedule-config"
})
class PickupCorporateScheduleConfigStore extends VuexModule {
  public isLoading = false;
  public isSubmitting = false;
  public errorType = "";
  public estimateAdditionalKoliError = "";
  public estimateWeightError = "";
  public pickupDatePickerConfig = new PickupCorporateConfig();
  public config = new PickupCorporateScheduleConfig();
  public showModalSuccess = false;
  public loadSequence = 0;

  @Mutation
  public setIsLoading(value: boolean): void {
    this.isLoading = value;
  }

  @Mutation
  public setErrorType(value: string): void {
    this.errorType = value;
  }

  @Mutation
  public setIsSubmitting(value: boolean): void {
    this.isSubmitting = value;
  }

  @Mutation
  public setShowModalSuccess(value: boolean): void {
    this.showModalSuccess = value;
  }

  @Mutation
  public setConfig(value: Partial<PickupCorporateScheduleConfig>): void {
    if (value.estimateAdditionalKoli !== undefined) {
      this.estimateAdditionalKoliError = "";
    }
    if (value.estimateWeight !== undefined) {
      this.estimateWeightError = "";
    }
    this.config = new PickupCorporateScheduleConfig({
      ...this.config,
      ...value
    });
  }

  @Mutation
  public setPickupDatePickerConfig(value: PickupCorporateConfig): void {
    this.pickupDatePickerConfig = value;
  }

  @Mutation
  public bumpLoadSequence(): void {
    this.loadSequence += 1;
  }

  @Mutation
  public setEstimateFieldErrors(value: {
    additionalKoli: string;
    weight: string;
  }): void {
    this.estimateAdditionalKoliError = value.additionalKoli;
    this.estimateWeightError = value.weight;
  }

  @Action
  public async fetchPickupDatePickerConfig(): Promise<void> {
    this.setIsLoading(true);
    try {
      const presenter = container.resolve(PickupCorporateConfigPresenter);
      this.setPickupDatePickerConfig(await presenter.getConfig());
    } catch {
      this.setPickupDatePickerConfig(new PickupCorporateConfig());
    } finally {
      this.setIsLoading(false);
    }
  }

  @Action
  public async loadConfig(clientId?: number): Promise<void> {
    this.setIsLoading(true);
    this.setErrorType("");
    this.bumpLoadSequence();
    const sequence = this.loadSequence;
    if (clientId) {
      this.setConfig(new PickupCorporateScheduleConfig());
    }
    try {
      const presenter = container.resolve(
        PickupCorporateScheduleConfigPresenter
      );
      const result = await presenter.loadConfig(clientId);
      if (sequence !== this.loadSequence) return;
      this.setConfig(result);
    } catch (error) {
      if (sequence !== this.loadSequence) return;
      this.setErrorType("PICKUP_SCHEDULE_CONFIG_LOAD_ERROR");
      const isMalaysia =
        AccountController.accountData.countryCode?.toUpperCase() === "MY";
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          isMalaysia
            ? "Failed to load Pick Up schedule configuration"
            : "Gagal memuat konfigurasi jadwal Pick Up",
          () => this.loadConfig
        )
      );
    } finally {
      if (sequence === this.loadSequence) {
        this.setIsLoading(false);
      }
    }
  }

  @Action({ rawError: true })
  public async saveConfig(
    config: PickupCorporateScheduleConfig
  ): Promise<boolean> {
    this.setIsSubmitting(true);
    this.setEstimateFieldErrors({ additionalKoli: "", weight: "" });
    try {
      const presenter = container.resolve(
        PickupCorporateScheduleConfigPresenter
      );
      const saved = await presenter.saveConfig(config);
      this.setConfig(config);
      const isMalaysia =
        AccountController.accountData.countryCode?.toUpperCase() === "MY";
      MainAppController.setSnackbarData(
        new Snackbar({
          type: "success",
          message: isMalaysia
            ? "Pick Up schedule successfully saved"
            : "Jadwal Pick Up berhasil disimpan"
        })
      );
      MainAppController.setAutoClose(true);
      MainAppController.setSnackbar(true);
      return saved;
    } catch (error) {
      const message = getBackendMessage(error);
      const normalizedMessage = message.toLowerCase();
      this.setEstimateFieldErrors({
        additionalKoli: normalizedMessage.includes("psestimatetotalkoli")
          ? message
          : "",
        weight: normalizedMessage.includes("psestimatetotaltonase")
          ? message
          : ""
      });
      const isMalaysia =
        AccountController.accountData.countryCode?.toUpperCase() === "MY";
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          isMalaysia
            ? "Failed to save Pick Up schedule"
            : "Gagal menyimpan jadwal Pick Up",
          () => this.saveConfig(config)
        )
      );
      return false;
    } finally {
      this.setIsSubmitting(false);
    }
  }
}

export const PickupCorporateScheduleConfigController = getModule(
  PickupCorporateScheduleConfigStore
);
